
import React, { useState } from 'react';
import { mockBookings } from '@/data/mockBookings';
import { 
  startOfMonth, 
  endOfMonth, 
  startOfYear, 
  endOfYear, 
  startOfDay, 
  endOfDay, 
  isWithinInterval,
  subMonths,
  format 
} from 'date-fns';
import { nb } from 'date-fns/locale';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/enhanced/select";
import { BookingStats as BookingStatsType, DateRange } from '@/types/booking';
import DateRangePicker from './DateRangePicker';

const BookingStats: React.FC = () => {
  const today = new Date();
  
  // Default to current month
  const [dateRange, setDateRange] = useState<DateRange>({
    from: startOfMonth(today),
    to: endOfMonth(today)
  });
  
  const [predefinedRange, setPredefinedRange] = useState<string>("this-month");
  
  // Calculate stats based on selected date range
  const stats: BookingStatsType = mockBookings.reduce((acc, booking) => {
    // Check if booking is within the selected date range
    const isInRange = isWithinInterval(booking.startDate, {
      start: startOfDay(dateRange.from),
      end: endOfDay(dateRange.to)
    }) || isWithinInterval(booking.endDate, {
      start: startOfDay(dateRange.from),
      end: endOfDay(dateRange.to)
    });
    
    if (!isInRange) return acc;
    
    return {
      totalBookings: acc.totalBookings + 1,
      activeBookings: acc.activeBookings + (booking.status === 'aktiv' ? 1 : 0),
      totalIncome: acc.totalIncome + booking.price,
      topPlacementCount: acc.topPlacementCount + (booking.placement === 'top' ? 1 : 0)
    };
  }, {
    totalBookings: 0,
    activeBookings: 0,
    totalIncome: 0,
    topPlacementCount: 0
  });
  
  // Format price with Norwegian currency format
  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('nb-NO', {
      style: 'currency',
      currency: 'NOK',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };
  
  // Handle selecting a predefined date range
  const handlePredefinedRangeChange = (value: string) => {
    setPredefinedRange(value);
    
    switch (value) {
      case "this-month":
        setDateRange({
          from: startOfMonth(today),
          to: endOfMonth(today)
        });
        break;
      case "last-month":
        const lastMonth = subMonths(today, 1);
        setDateRange({
          from: startOfMonth(lastMonth),
          to: endOfMonth(lastMonth)
        });
        break;
      case "last-3-months":
        setDateRange({
          from: startOfMonth(subMonths(today, 3)),
          to: endOfMonth(today)
        });
        break;
      case "this-year":
        setDateRange({
          from: startOfYear(today),
          to: endOfYear(today)
        });
        break;
      case "custom":
        // Keep current custom range
        break;
    }
  };
  
  // Handle custom date range selection
  const handleCustomDateChange = (range: DateRange | undefined) => {
    if (!range?.from) return;
    
    setDateRange({
      from: range.from,
      to: range.to || range.from
    });
    setPredefinedRange("custom");
  };
  
  return (
    <div>
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mb-6">
        <div className="lg:col-span-2">
          <label className="text-sm font-medium mb-1 block">Periode</label>
          <Select value={predefinedRange} onValueChange={handlePredefinedRangeChange}>
            <SelectTrigger>
              <SelectValue placeholder="Velg periode" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="this-month">Denne måneden</SelectItem>
              <SelectItem value="last-month">Forrige måned</SelectItem>
              <SelectItem value="last-3-months">Siste 3 måneder</SelectItem>
              <SelectItem value="this-year">Dette året</SelectItem>
              <SelectItem value="custom">Egendefinert periode</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="lg:col-span-2">
          <label className="text-sm font-medium mb-1 block">Egendefinert periode</label>
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={handleCustomDateChange}
          />
        </div>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total inntekt
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatPrice(stats.totalIncome)}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {format(dateRange.from, 'dd. MMM', { locale: nb })} - {format(dateRange.to, 'dd. MMM yyyy', { locale: nb })}
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Antall bookinger
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalBookings}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {stats.activeBookings} aktive nå
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Topp 10 plasseringer
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.topPlacementCount}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {((stats.topPlacementCount / (stats.totalBookings || 1)) * 100).toFixed(0)}% av totalt
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Gjennomsnittlig pris
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatPrice(stats.totalBookings > 0 ? stats.totalIncome / stats.totalBookings : 0)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Per booking i perioden
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BookingStats;
